import { useEffect, useRef } from 'react';

import { isScrollableY } from '@/util/dom';

const findScrollContainer = (root: Element): HTMLElement | null => {
  if (root instanceof HTMLElement && isScrollableY(root)) {
    return root;
  }
  for (const child of Array.from(root.children)) {
    const found = findScrollContainer(child);
    if (found) {
      return found;
    }
  }
  return null;
};

export const useForwardWheelScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    let scrollTarget: HTMLElement | null = null;
    const resolveScrollTarget = () => {
      if (scrollTarget?.isConnected) {
        return scrollTarget;
      }
      for (const sibling of Array.from(el.parentElement?.children ?? [])) {
        if (sibling === el) {
          continue;
        }
        const found = findScrollContainer(sibling);
        if (found) {
          scrollTarget = found;
          return found;
        }
      }
      return null;
    };

    let innerTarget: HTMLElement | null = null;
    const resolveInnerTarget = () => {
      if (innerTarget?.isConnected && isScrollableY(innerTarget)) {
        return innerTarget;
      }
      innerTarget = findScrollContainer(el);
      return innerTarget;
    };

    const onWheel = (e: WheelEvent) => {
      const inner = resolveInnerTarget();
      if (inner && e.target instanceof Node && inner.contains(e.target)) {
        return;
      }

      const target = resolveScrollTarget();
      if (target) {
        target.scrollTop += e.deltaY;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return containerRef;
};
